"use client"

import type React from "react"
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react"
import type { CursorContextType, CursorConfig, CursorState } from "../types"
import { mouseStore } from "../mouseStore"

export const defaultCursorConfig: CursorConfig = {
  default: {
    width: 24,
    height: 24,
    scale: 1,
    shape: "circle",
    color: "rgba(255, 255, 255, 0.8)",
    opacity: 1,
    borderColor: "rgba(0, 0, 0, 0)",
    borderWidth: 0,
    borderStyle: "solid",
    label: "",
    fontSize: "12px",
    fontWeight: "bold",
    labelColor: "#000",
    transition: {
      stiffness: 800,
      damping: 80,
      mass: 1,
    },
  },
}

const CursorContext = createContext<CursorContextType>({
  cursorState: { ...defaultCursorConfig.default, variant: "default" },
  setCursorVariant: () => {},
  config: defaultCursorConfig,
  mousePosition: { x: 0, y: 0 },
  resetCursorToDefault: () => {},
  setGlobalCursorVariant: () => {},
  clearGlobalCursorVariant: () => {},
})

export const useCursorContext = () => useContext(CursorContext)

interface CursorProviderProps {
  children: ReactNode
  config?: CursorConfig
  hideNativeCursor?: boolean
}

export const CursorProvider: React.FC<CursorProviderProps> = ({
  children,
  config = defaultCursorConfig,
  hideNativeCursor = true,
}) => {
  const [variantStack, setVariantStack] = useState<string[]>(["default"])
  const [globalVariant, setGlobalVariant] = useState<string | null>(null)

  // All variant setters are stable (useCallback) so hooks that include them
  // in effect deps (useCursorIdle, useCursorZone) don't re-run every frame.
  const setCursorVariant = useCallback((variant: string, mode: "push" | "pop" | "reset" = "push") => {
    if (mode === "reset") {
      setVariantStack(["default"])
    } else if (mode === "push") {
      setVariantStack((prev) => [...prev, variant])
    } else {
      setVariantStack((prev) => {
        const next = prev.slice(0, -1)
        return next.length ? next : ["default"]
      })
    }
  }, [])

  const resetCursorToDefault = useCallback(() => {
    setVariantStack(["default"])
  }, [])

  const setGlobalCursorVariant = useCallback((variant: string) => {
    setGlobalVariant(variant)
  }, [])

  const clearGlobalCursorVariant = useCallback(() => {
    setGlobalVariant(null)
  }, [])

  // Hide native cursor
  useEffect(() => {
    if (!hideNativeCursor) return
    const style = document.createElement("style")
    style.textContent = "body,a,button,[role=\"button\"],*{cursor:none!important}"
    document.head.appendChild(style)
    return () => { document.head.removeChild(style) }
  }, [hideNativeCursor])

  // Context value is only recreated when the cursor VARIANT or config changes —
  // never on mouse move. This stops 60fps re-renders of all context consumers
  // (CursorTarget, useMagnetic, useCursorIdle, useCursorZone, etc.).
  const contextValue = useMemo(() => {
    const topVariant = globalVariant ?? variantStack[variantStack.length - 1]
    const currentConfig = config[topVariant] ?? config.default
    const cursorState: CursorState = { ...currentConfig, variant: topVariant }

    return {
      cursorState,
      setCursorVariant,
      resetCursorToDefault,
      config,
      // Snapshot at render time. For live position use mouseStore directly
      // or the useMousePosition() hook. @deprecated for reactive use.
      mousePosition: { x: mouseStore.x, y: mouseStore.y },
      setGlobalCursorVariant,
      clearGlobalCursorVariant,
    }
  }, [
    variantStack,
    globalVariant,
    config,
    setCursorVariant,
    resetCursorToDefault,
    setGlobalCursorVariant,
    clearGlobalCursorVariant,
  ])

  return (
    <CursorContext.Provider value={contextValue}>
      {children}
    </CursorContext.Provider>
  )
}
